/**
 * Noten (F-22 Stufe 4): je Fach der gewichtete Schnitt und alle Noten direkt
 * sichtbar, farbig nach der einen Bewertungsskala (D-04) - mit Zahl.
 */
import { useState } from 'react';
import { Plus, X } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { AreaDot } from '@/modules/areas/AreaDot';
import { addGrade, deleteGrade } from '@/db/repositories/grades';
import {
  NOTEN_ARTEN, formatNote, gesamtSchnitt, gueltigerWert, inPunkte, schnittPunkte, skalaVonPunkten,
} from '@/domain/school/grades';
import { skalaFarbe } from '@/domain/rating';
import { formatDate, today } from '@/lib/datetime';
import type { Grade, Notensystem, Subject } from '@/domain/types';

/** Ein Wert mit Farbe der Skala: farbiger Rand links, Zahl immer lesbar. */
export function NotenWert({ punkte, system, nachkomma = 1, titel }: {
  punkte: number; system: Notensystem; nachkomma?: number; titel?: string;
}) {
  return (
    <span
      title={titel}
      className="inline-flex shrink-0 items-center rounded border-l-[3px] bg-surface-raised px-1.5 py-px text-[12px] font-medium tabular-nums"
      style={{ borderLeftColor: skalaFarbe(skalaVonPunkten(punkte)), color: skalaFarbe(skalaVonPunkten(punkte)) }}
    >
      {formatNote(punkte, system, nachkomma)}
    </span>
  );
}

export function Noten({ faecher, noten, system }: { faecher: Subject[]; noten: Grade[]; system: Notensystem }) {
  const schnitte = faecher.map((f) => schnittPunkte(noten.filter((n) => n.subjectId === f.id)));
  const gesamt = gesamtSchnitt(schnitte);

  return (
    <section aria-label="Noten" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <header className="flex items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">Noten</h2>
        {gesamt !== null && (
          <span className="flex items-center gap-1.5 text-[12px] text-ink-muted">
            Gesamt <NotenWert punkte={gesamt} system={system} />
          </span>
        )}
      </header>

      <ul className="flex flex-col gap-2">
        {faecher.map((f, i) => {
          const eigene = noten.filter((n) => n.subjectId === f.id);
          const schnitt = schnitte[i]!;
          return (
            <li key={f.id} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <AreaDot color={f.color} size="sm" />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                {schnitt === null
                  ? <span className="text-[12px] text-ink-faint">noch keine Noten</span>
                  : <NotenWert punkte={schnitt} system={system} titel="Gewichteter Durchschnitt" />}
              </div>
              {eigene.length > 0 && (
                <ul className="flex flex-wrap gap-1 pl-4">
                  {eigene.map((n) => (
                    <li key={n.id} className="group relative">
                      <NotenWert
                        punkte={inPunkte(n)}
                        system={system}
                        nachkomma={n.system === system && Number.isInteger(n.value) ? 0 : 1}
                        titel={`${n.art} · ${formatDate(n.date)} · Gewicht ${n.weight}${n.note ? ` · ${n.note}` : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => { if (window.confirm(`${n.art} vom ${formatDate(n.date)} löschen? Kommt für 30 Tage in den Papierkorb.`)) void deleteGrade(n.id); }}
                        aria-label={`${f.name}: ${n.art} vom ${formatDate(n.date)} löschen`}
                        className="absolute -right-1.5 -top-1.5 grid size-4 place-items-center rounded-full bg-surface text-ink-faint opacity-0 ring-1 ring-line hover:text-danger focus:opacity-100 group-hover:opacity-100"
                      >
                        <X className="size-2.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {faecher.length > 0 && <NeueNoteForm faecher={faecher} system={system} />}
    </section>
  );
}

function NeueNoteForm({ faecher, system }: { faecher: Subject[]; system: Notensystem }) {
  const [fach, setFach] = useState('');
  const [art, setArt] = useState(NOTEN_ARTEN[0]!.art);
  const [gewicht, setGewicht] = useState(String(NOTEN_ARTEN[0]!.gewicht));
  const [wert, setWert] = useState('');
  const [datum, setDatum] = useState(today());
  const fachId = fach || faecher[0]!.id;
  const zahl = Number(wert.replace(',', '.'));
  const gueltig = wert.trim() !== '' && gueltigerWert(zahl, system) && Number(gewicht.replace(',', '.')) > 0;

  async function anlegen() {
    if (!gueltig) return;
    if (await addGrade({ subjectId: fachId, date: datum, value: zahl, system, weight: Number(gewicht.replace(',', '.')), art })) setWert('');
  }

  const feld = 'h-9 rounded-md border border-line bg-surface px-2 text-sm';
  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-line pt-2.5">
      <select value={fachId} onChange={(e) => setFach(e.target.value)} aria-label="Fach der Note" className={feld}>
        {faecher.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <select
        value={art}
        onChange={(e) => {
          setArt(e.target.value);
          setGewicht(String(NOTEN_ARTEN.find((a) => a.art === e.target.value)?.gewicht ?? 1));
        }}
        aria-label="Art der Note"
        className={feld}
      >
        {NOTEN_ARTEN.map((a) => <option key={a.art} value={a.art}>{a.art}</option>)}
      </select>
      <div className="w-20">
        <Input value={wert} inputMode="decimal" onChange={(e) => setWert(e.target.value)} aria-label={system === 'punkte' ? 'Punkte (0–15)' : 'Note (1–6)'}
          placeholder={system === 'punkte' ? '0–15' : '1–6'} className="h-9 text-sm"
          onKeyDown={(e) => { if (e.key === 'Enter') void anlegen(); }} />
      </div>
      <div className="flex w-20 items-center gap-1" title="Gewicht im Durchschnitt">
        <span className="text-[12px] text-ink-faint" aria-hidden="true">×</span>
        <Input value={gewicht} inputMode="decimal" onChange={(e) => setGewicht(e.target.value)} aria-label="Gewicht" title="Gewicht im Durchschnitt" className="h-9 text-sm" />
      </div>
      <div className="w-40"><Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} aria-label="Datum der Note" className="h-9 text-sm" /></div>
      <Button size="sm" variant="primary" disabled={!gueltig} onClick={() => void anlegen()}>
        <Plus className="size-3.5" /> Note
      </Button>
    </div>
  );
}
